import React, { useEffect, useCallback, useState } from 'react';
import { useProgressContext } from '../../contexts/ProgressContext';
import { useUIContext } from '../../contexts/UIContext';
import Icon from '../common/Icon';
import BaseModal from './BaseModal'; // Import BaseModal for consistency

const ProfilePickerModal = ({ isOpen, onClose }) => {
    const { progress, activeProfile } = useProgressContext();
    const { setPage, triggerSwitchProfile } = useUIContext();
    const [selectedProfileId, setSelectedProfileId] = useState(null);

    // Reset selected profile when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedProfileId(null); // Reset selection state when modal opens
        }
    }, [isOpen]);

    // Handle cleanup when modal opens/closes - CRITICAL for preventing button timeouts
    useEffect(() => {
        if (!isOpen) return;

        // Prevent background scrolling
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // Handle escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);

        // CRITICAL CLEANUP - Prevents button timeout issues
        return () => {
            document.body.style.overflow = originalOverflow;
            document.removeEventListener('keydown', handleEscape);
            
            // Force cleanup any lingering active states
            setTimeout(() => {
                const activeElements = document.querySelectorAll('[aria-expanded="true"]');
                activeElements.forEach(el => el.setAttribute('aria-expanded', 'false'));
                
                // Remove any stuck focus states
                const focusedElements = document.querySelectorAll(':focus');
                focusedElements.forEach(el => el.blur());
            }, 100);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const profiles = Object.values(progress?.profiles || {});

    const handleProfileSelect = (profile) => {
        if (profile.id === activeProfile?.id) {
            onClose();
            return;
        }
        
        // Set selected profile immediately to update UI
        setSelectedProfileId(profile.id);
        
        // Close the profile picker modal first
        onClose();
        // Then trigger the switch profile confirmation modal
        triggerSwitchProfile(profile);
    };

    const handleAddProfile = () => {
        onClose();
        setPage('add_new_profile');
    };

    const getLanguageDisplay = (profile) => {
        const language = profile.currentLanguage || 'Twi';
        return `Learning ${language}`;
    };

    // Check if device is mobile
    const isMobile = window.innerWidth < 1024;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl p-6 mx-4 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">
                    Who is learning?
                </h2>

                {/* Profile List */}
                <div className="space-y-3 mb-6">
                    {profiles.map(profile => {
                        // Use selectedProfileId if set (during transition), otherwise use activeProfile
                        const isActive = selectedProfileId 
                            ? profile.id === selectedProfileId 
                            : profile.id === activeProfile?.id;
                        
                        return (
                            <div
                                key={profile.id}
                                onClick={() => handleProfileSelect(profile)}
                                className={`p-4 rounded-xl cursor-pointer transition-all ${
                                    isActive
                                        ? 'bg-blue-50 border-2 border-blue-200'
                                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                                }`}
                            >
                                <div className="flex items-center space-x-4">
                                    <div className="w-10 h-10 flex items-center justify-center">
                                        <Icon name="user" className="text-gray-600 text-lg" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-semibold text-gray-800">
                                            {profile.childName || profile.name}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {getLanguageDisplay(profile)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Add New Profile Button - Only show if less than 4 profiles */}
                {profiles.length < 4 && (
                    <button
                        onClick={handleAddProfile}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors flex items-center justify-center space-x-2 mb-3"
                    >
                        <Icon name="add" className="text-white" />
                        <span>Add New Profile</span>
                    </button>
                )}

                {/* Cancel Button */}
                <button
                    onClick={onClose}
                    className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default ProfilePickerModal;
